import React, { useState, useEffect } from "react";
import axios from "axios";
import Table from "../../Table/Table";
import {
  Button,
  TextField,
  CircularProgress,
  Typography,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogContentText,
  DialogActions,
  Alert,
  Snackbar,
  Box,
  Paper,
  Chip,
  LinearProgress,
} from "@mui/material";
import {
  Download as DownloadIcon,
  Description as ReportIcon,
  Archive as ZipIcon,
  WarningAmber as WarningIcon,
} from "@mui/icons-material";

const Live = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [order, setOrder] = useState("desc");
  const [orderBy, setOrderBy] = useState("issue_date");

  const [openConfirm, setOpenConfirm] = useState(false);
  const [selectedRow, setSelectedRow] = useState(null);
  const [snackbar, setSnackbar] = useState({ open: false, message: "", severity: "success" });

  const baseURL = process.env.REACT_APP_API_BASE_URL;

  const headers = [
    { dbParam: "ncr_number", displayName: "NCR No" },
    { dbParam: "total_reject_qty", displayName: "Module Count" },
    { dbParam: "supplier", displayName: "Supplier" },
    { dbParam: "fileUrl", displayName: "Report" },
    { dbParam: "zipUrl", displayName: "Images ZIP" },
    { dbParam: "issue_date", displayName: "Issue Date" },
    { dbParam: "initiator", displayName: "Created By" },
    { dbParam: "status", displayName: "Action" },
    { dbParam: "remarks", displayName: "Closure Remarks" },
  ];

  useEffect(() => {
    const fetchNCRList = async () => {
      const selectedProject = localStorage.getItem("selectedProject");
      if (!selectedProject) {
        setError("No project selected.");
        setLoading(false);
        return;
      }

      try {
        setLoading(true);
        const response = await axios.post(`${baseURL}/ncr-list`, { projectId: selectedProject });

        if (response.data.success && Array.isArray(response.data.data)) {
          const openNCRs = response.data.data
            .filter((item) => item.status === "Open")
            .map((item) => ({
              id: item.id,
              ncr_number: item.ncr_number,
              total_reject_qty: item.total_reject_qty,
              supplier: item.supplier,
              issue_date: item.issue_date,
              initiator: item.initiator,
              status: "Open",
              remarks: "",
            }));

          setData(openNCRs);
        }
      } catch (err) {
        setError("Failed to load NCR data.");
        console.error(err);
      } finally {
        setLoading(false);
      }
    };

    fetchNCRList();
  }, [baseURL]);

  const handleSort = (property) => {
    const isAsc = orderBy === property && order === "asc";
    setOrder(isAsc ? "desc" : "asc");
    setOrderBy(property);
  };

  const sortedData = [...data].sort((a, b) => {
    let aVal = a[orderBy];
    let bVal = b[orderBy];
    if (orderBy === "issue_date") {
      aVal = new Date(aVal);
      bVal = new Date(bVal);
    }
    return (order === "asc" ? aVal < bVal : aVal > bVal) ? -1 : 1;
  });

  const handleRemarksChange = (id, value) => {
    setData((prev) => prev.map((item) => (item.id === id ? { ...item, remarks: value } : item)));
  };

  const downloadReport = async (ncrNumber) => {
    try {
      const response = await axios.post(
        `${baseURL}generate-ncr-report`,
        { ncr_number: ncrNumber },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], {
        type: "application/vnd.openxmlformats-officedocument.wordprocessingml.document",
      });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ncrNumber.replace(/\//g, "_")}_Report.docx`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to download report.", severity: "error" });
    }
  };

  const downloadZip = async (ncrNumber) => {
    try {
      const response = await axios.post(
        `${baseURL}/ncr-images-zip`,
        { ncr_number: ncrNumber },
        { responseType: "blob" }
      );
      const blob = new Blob([response.data], { type: "application/zip" });
      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = `${ncrNumber.replace(/\//g, "_")}_Images.zip`;
      link.click();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      setSnackbar({ open: true, message: "Failed to download ZIP.", severity: "error" });
    }
  };

  const handleCloseAttempt = (row) => {
    if (!row.remarks?.trim()) {
      setSnackbar({
        open: true,
        message: "Please provide closure remarks before closing the NCR.",
        severity: "warning",
      });
      return;
    }
    setSelectedRow(row);
    setOpenConfirm(true);
  };
  const username = sessionStorage.getItem("username");
  const confirmCloseNCR = async () => {
    try {
      const response = await axios.post(`${baseURL}/close-ncr`, {
        ncr_number: selectedRow.ncr_number,
        closed_by: username,
        closed_date: new Date().toISOString().split("T")[0],
        remarks: selectedRow.remarks.trim(),
      });

      if (response.data.success) {
        setData((prev) => prev.filter((item) => item.id !== selectedRow.id));
        setSnackbar({
          open: true,
          message: response.data.message || `NCR ${selectedRow.ncr_number} closed successfully.`,
          severity: "success",
        });
      }
    } catch (err) {
      setSnackbar({
        open: true,
        message: err.response?.data?.message || "Failed to close NCR.",
        severity: "error",
      });
    } finally {
      setOpenConfirm(false);
      setSelectedRow(null);
    }
  };

  const renderCell = (header, row) => {
    if (header.dbParam === "fileUrl") {
      return (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ReportIcon />}
          onClick={() => downloadReport(row.ncr_number)}
          sx={{
            borderColor: "#94a3b8",
            color: "#475569",
            "&:hover": { borderColor: "#64748b", bgcolor: "#f8fafc" },
          }}
        >
          Report
        </Button>
      );
    }

    if (header.dbParam === "zipUrl") {
      return (
        <Button
          variant="outlined"
          size="small"
          startIcon={<ZipIcon />}
          onClick={() => downloadZip(row.ncr_number)}
          sx={{
            borderColor: "#94a3b8",
            color: "#475569",
            "&:hover": { borderColor: "#64748b", bgcolor: "#f8fafc" },
          }}
        >
          ZIP
        </Button>
      );
    }

    if (header.dbParam === "status") {
      return (
        <Button
          variant="contained"
          size="small"
          color="error"
          startIcon={<WarningIcon />}
          onClick={() => handleCloseAttempt(row)}
          sx={{ bgcolor: "#dc2626", "&:hover": { bgcolor: "#b91c1c" } }}
        >
          Close NCR
        </Button>
      );
    }

    if (header.dbParam === "remarks") {
      return (
        <TextField
          fullWidth
          multiline
          rows={3}
          value={row.remarks}
          onChange={(e) => handleRemarksChange(row.id, e.target.value)}
          placeholder="Enter closure remarks (required)"
          variant="outlined"
          size="small"
          sx={{
            "& .MuiOutlinedInput-root": {
              fontSize: "12px",
              bgcolor: row.remarks ? "#f0fdf4" : "#fffbeb",
              "&:hover": { bgcolor: row.remarks ? "#ecfdf5" : "#fefce8" },
            },
          }}
        />
      );
    }

    return null;
  };

  return (
    <Box sx={{ p: { xs: 2, md: 4 }, bgcolor: "#f8fafc", minHeight: "100vh" }}>
      <Paper elevation={0} sx={{ borderRadius: 3, border: "1px solid #e2e8f0", overflow: "hidden" }}>
        {/* Calm, elegant header */}
        <Box
          sx={{
            bgcolor: "#ffffff",
            borderBottom: "1px solid #e2e8f0",
            p: 4,
            background: "linear-gradient(to bottom, #ffffff, #f8fafc)",
          }}
        >
          <Typography variant="h4" fontWeight="600" color="#1e293b">
            Live NCR Dashboard
          </Typography>
          <Typography variant="subtitle1" sx={{ mt: 1, color: "#64748b" }}>
            Monitoring Open Non-Conformance Reports
          </Typography>
        </Box>

        <Box sx={{ p: 4 }}>
          {loading ? (
            <Box sx={{ textAlign: "center", py: 8 }}>
              <CircularProgress size={48} thickness={4} sx={{ color: "#64748b" }} />
              <Typography variant="h6" sx={{ mt: 3, color: "#64748b" }}>
                Loading open NCRs...
              </Typography>
              <LinearProgress sx={{ mt: 4, borderRadius: 2, height: 6 }} />
            </Box>
          ) : error ? (
            <Alert severity="error" sx={{ borderRadius: 2, border: "1px solid #fee2e2" }}>
              {error}
            </Alert>
          ) : data.length === 0 ? (
            <Paper sx={{ p: 8, textAlign: "center", bgcolor: "#ffffff", border: "1px dashed #cbd5e1" }}>
              <Typography variant="h6" color="#475569">
                No open NCRs
              </Typography>
              <Typography variant="body1" sx={{ mt: 1, color: "#94a3b8" }}>
                All non-conformances have been addressed or none reported yet.
              </Typography>
            </Paper>
          ) : (
            <>
              <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center", mb: 4 }}>
                <Typography variant="h6" fontWeight="500" color="#1e293b">
                  {data.length} Open Non-Conformance Report{data.length === 1 ? "" : "s"}
                </Typography>
              </Box>

              <Table
                tableHeaders={headers}
                tableData={sortedData}
                order={order}
                orderBy={orderBy}
                handleSort={handleSort}
                renderCell={renderCell}
              />
            </>
          )}
        </Box>
      </Paper>

      {/* Calm Confirmation Dialog */}
      <Dialog open={openConfirm} onClose={() => setOpenConfirm(false)} maxWidth="sm" fullWidth>
        <DialogTitle sx={{ bgcolor: "#fef2f2", color: "#991b1b", fontWeight: "600" }}>
          Confirm Closure
        </DialogTitle>
        <DialogContent sx={{ pt: 3 }}>
          <DialogContentText color="#1e293b">
            You are about to permanently close this NCR:
          </DialogContentText>
          <Typography variant="subtitle1" fontWeight="bold" sx={{ mt: 2, color: "#1e293b" }}>
            {selectedRow?.ncr_number}
          </Typography>
          <Typography variant="body2" sx={{ mt: 2, color: "#475569" }}>
            <strong>Remarks:</strong> "{selectedRow?.remarks}"
          </Typography>
        </DialogContent>
        <DialogActions sx={{ p: 3 }}>
          <Button onClick={() => setOpenConfirm(false)} sx={{ color: "#64748b" }}>
            Cancel
          </Button>
          <Button onClick={confirmCloseNCR} variant="contained" color="error">
            Close Permanently
          </Button>
        </DialogActions>
      </Dialog>

      {/* Clean Snackbar */}
      <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={() => setSnackbar({ ...snackbar, open: false })}
        anchorOrigin={{ vertical: "top", horizontal: "center" }}
      >
        <Alert
          onClose={() => setSnackbar({ ...snackbar, open: false })}
          severity={snackbar.severity}
          variant="filled"
          sx={{ borderRadius: 2, boxShadow: 4 }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Live;